import { hasIntegrationAtom, integrationDataAtom } from '@/atoms/integrationAtoms';
import { setSidebarOpenTab, SidebarTab } from '@/atoms/persistedPreferencesAtoms';
import { IS_INSET_DESIGN_ENABLED } from '@/components/constants/flags';
import useQueryUserTier from '@/hooks/query/useQueryUserTier';
import { useEvidenceAnalytics } from '@/hooks/useEvidenceAnalytics';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { useKinde } from '@/hooks/useKinde';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import { Button } from '@heidi/design-system/button';
import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import { isOpenSessionRoute } from '@heidi/session/contracts';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { History, MenuIcon, SquarePen } from 'lucide-react';
import { useRouter } from 'next/router';
import { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';

import { useValidateEpicLaunch } from '@/hooks/integrations/useValidateEpicLaunch';
import { cn } from '@/lib/cn';
import { HeidiLogoMark } from '@heidi/design-system/heidi-logo-mark';
import IntegrationMobileAppHeader from './integration-mobile-app-header';
import {
  canCollapseAtom,
  mobileChatHistoryOpenAtom,
  mobileHeaderActionsAtom,
  mobilePageHeaderOwnersAtom,
} from './mobile-app-header.atoms';
import { mobileAppHeaderMessages } from './mobile-app-header.messages';
import { MobileChatHistoryDrawer } from './mobile-chat-history-drawer';
import { isMobileHeaderSheetOpenAtom } from './mobile-header-sheet-atoms';
import MobileHeaderSheetContent from './mobile-header-sheet-content';

/** Page-specific actions to render in the mobile header (right side of hamburger row). */

const MobileAppHeader = () => {
  const intl = useIntl();
  const isMobile = useIsMobile();
  const router = useRouter();
  const kindeAuth = useKinde();
  const { isValidEpicLaunch } = useValidateEpicLaunch();

  const [historyDrawerOpen, setHistoryDrawerOpen] = useAtom(mobileChatHistoryOpenAtom);
  const pageOwnsHeader = useAtomValue(mobilePageHeaderOwnersAtom) > 0;

  const { userTier } = useQueryUserTier();
  const { trackNewChatClicked } = useEvidenceAnalytics();
  const openNavigationMenuLabel = intl.formatMessage(
    mobileAppHeaderMessages.openNavigationMenu
  );
  const chatHistoryTitle = intl.formatMessage(mobileAppHeaderMessages.chatHistory);
  const newChatLabel = intl.formatMessage(mobileAppHeaderMessages.newChat);

  const { isAuthenticated } = useIsAuthenticated();
  const hasIntegration = useAtomValue(hasIntegrationAtom);
  const integrationData = useAtomValue(integrationDataAtom);

  const setCanCollapse = useSetAtom(canCollapseAtom);
  const mobileHeaderActions = useAtomValue(mobileHeaderActionsAtom);

  // Check if we're on the full-screen evidence route
  const isOnFullScreenEvidence =
    router.pathname.includes('/scribe/chat') || router.pathname.includes('/chat');
  const showEvidenceActions = isMobile && isAuthenticated && isOnFullScreenEvidence;

  // !Error: use atomWithHash if possible
  const [isMobileHeaderSheetOpen, setIsMobileHeaderSheetOpen] = useAtom(
    isMobileHeaderSheetOpenAtom
  );

  const toggle = useCallback(() => {
    if (isMobile) {
      setIsMobileHeaderSheetOpen(!isMobileHeaderSheetOpen);
    }
  }, [isMobile, isMobileHeaderSheetOpen, setIsMobileHeaderSheetOpen]);

  const handleNewChat = useCallback(() => {
    trackNewChatClicked('header');
    const basePath = router.pathname.includes('/scribe/chat') ? '/scribe/chat' : '/chat';
    void router.push(basePath, undefined, { shallow: true });
  }, [router, trackNewChatClicked]);

  const handleCloseHistoryDrawer = useCallback(() => {
    setHistoryDrawerOpen(false);
  }, [setHistoryDrawerOpen]);

  useEffect(() => () => setHistoryDrawerOpen(false), [setHistoryDrawerOpen]);

  // Crossing to desktop unmounts the drawer without an `onClose`, so the atom
  // would reopen it the next time the viewport is phone-width.
  useEffect(() => {
    if (!isMobile) setHistoryDrawerOpen(false);
  }, [isMobile, setHistoryDrawerOpen]);

  // The route id, not just the route. `/scribe/sessions` and
  // `/scribe/sessions/abc` share one pathname (optional catch-all), and this
  // header's usual mount condition is precisely the no-session case — so a
  // pathname-only guard would auto-open the sheet on the empty list. Normalised
  // to a primitive so it can sit in the effect deps without churning: it is
  // empty until `router.isReady`, and the effect has to re-run when it lands.
  const routeSessionId = Array.isArray(router.query.id)
    ? router.query.id[0]
    : router.query.id;

  useEffect(() => {
    if (
      hasIntegration &&
      kindeAuth.isAuthenticated &&
      userTier === SubscriptionTier.PRO_PLUS &&
      !integrationData.patientId &&
      isOpenSessionRoute(router.pathname, routeSessionId)
    ) {
      if (isMobile) {
        setIsMobileHeaderSheetOpen(true);
      }
      setSidebarOpenTab(SidebarTab.SESSION_LIST);
      setCanCollapse(false);
    } else {
      setCanCollapse(true);
    }
  }, [
    hasIntegration,
    integrationData,
    kindeAuth,
    userTier,
    router,
    routeSessionId,
    setIsMobileHeaderSheetOpen,
    setCanCollapse,
    isMobile,
  ]);

  if (isValidEpicLaunch) {
    return <IntegrationMobileAppHeader />;
  }

  return (
    <>
      {/* Mobile header bar with hamburger menu button */}
      {!pageOwnsHeader && (
        <div
          className={cn(
            'flex w-full flex-col gap-4 px-4 py-3 sm:hidden',
            IS_INSET_DESIGN_ENABLED() ? 'bg-surface-shell' : 'bg-surface-page'
          )}
        >
          <div className="flex flex-col gap-2">
            <div className="flex w-full flex-row items-center justify-between gap-3">
              <Button
                variant="ghost"
                size="lg"
                iconOnly
                onClick={toggle}
                data-testid="sidebar-mobile-burger"
                aria-label={openNavigationMenuLabel}
              >
                <MenuIcon />
              </Button>
              {isMobile && router.pathname.startsWith('/integrations') && (
                <>
                  <HeidiLogoMark className="size-4" aria-label="Heidi" />
                  <div className="size-6" />
                </>
              )}
              {/* Page-specific actions injected via mobileHeaderActionsAtom */}
              {!showEvidenceActions && mobileHeaderActions}
              {/* Evidence actions on the right side: specialty, history, new chat */}
              {showEvidenceActions && (
                <div className="flex flex-row items-center gap-2">
                  <Button
                    variant="ghost"
                    size="lg"
                    iconOnly
                    onClick={() => setHistoryDrawerOpen(true)}
                    aria-label={chatHistoryTitle}
                    aria-expanded={historyDrawerOpen}
                    aria-haspopup="dialog"
                    data-testid="chat-history-button"
                  >
                    <History />
                  </Button>
                  <Button
                    variant="ghost"
                    size="lg"
                    iconOnly
                    onClick={handleNewChat}
                    aria-label={newChatLabel}
                    data-testid="chat-new-chat-button"
                  >
                    <SquarePen />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <MobileChatHistoryDrawer
        open={historyDrawerOpen && isMobile}
        onClose={handleCloseHistoryDrawer}
      />
      {/* Vaul drawer - renders its own portal */}
      <MobileHeaderSheetContent />
    </>
  );
};

export default MobileAppHeader;
